/**
 * How good is tile recognition, really? Every scan proposes a hand and a person
 * then corrects it before saving the round, so the finished round is the
 * "answer key": comparing what the scanner proposed with what was finally
 * saved tells us how many tiles it got right — for either engine.
 *
 * Flow:
 *  1. A scan succeeds -> `notePendingRecognition` remembers what it proposed,
 *     keyed by the photo (the same photo string is attached to the round).
 *  2. The round is saved with that photo -> `takeRecognitionFor` retrieves the
 *     proposal, and `logRecognitionResult` compares it with the saved hand.
 *     A guest's phone can't log it (the admin looks at the host device), so it
 *     sends the proposal along with the round and the host logs it.
 *  3. A scan that never becomes a round (retaken, abandoned) is counted as
 *     "discarded" once it is old enough that it clearly isn't coming.
 *
 * Kept on this device only (localStorage), like the usage log — there is no
 * server-side record of it.
 */

import { isBonusTile, isTile, type BonusTile, type Tile } from '../rules/tiles';
import type { RecognitionEngine } from './recognitionEngine';

/** What a scan proposed for the hand. */
export interface RecognitionSnapshot {
  engine: RecognitionEngine;
  timestamp: number;
  /** Every tile proposed for the hand, claimed sets flattened in. */
  tiles: Tile[];
  flowers: BonusTile[];
  /** Tile values the scan marked "double-check against the photo". */
  flagged: Tile[];
}

export type AccuracyEntry =
  | {
      kind: 'used';
      engine: RecognitionEngine;
      timestamp: number;
      /** Tiles in the saved hand. */
      final: number;
      /** Proposed tiles that are not in the saved hand. */
      wrong: number;
      /** Saved-hand tiles the scan did not propose. */
      missing: number;
      /** How many of the wrong tiles had been flagged for double-checking. */
      wrongFlagged: number;
    }
  | { kind: 'miss'; engine: RecognitionEngine; timestamp: number; reason: 'no-hand' | 'error' }
  | { kind: 'unused'; engine: RecognitionEngine; timestamp: number };

/** The bits of `Storage` used here — lets tests pass a plain object. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const LOG_KEY = 'mahjong-scorer:recognitionAccuracyLog';
const PENDING_KEY = 'mahjong-scorer:recognitionPending';
const MAX_ENTRIES = 500;
const MAX_PENDING = 20;
/** A scan not saved as a round within this long is counted as discarded. */
export const PENDING_TTL_MS = 6 * 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// Pure pieces
// ---------------------------------------------------------------------------

/** A short, stable id for a photo (its data URL is far too long to key by): FNV-1a over every character, plus the length. */
export function photoKey(photo: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < photo.length; i++) {
    hash ^= photo.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `${photo.length}:${hash.toString(16)}`;
}

function countTiles(tiles: readonly string[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const t of tiles) counts.set(t, (counts.get(t) ?? 0) + 1);
  return counts;
}

/**
 * Compares a proposal with the saved hand as two piles of tiles (order and
 * grouping are irrelevant — the scan proposes one flat pile). A tile swapped
 * for another counts once as wrong and once as missing.
 */
export function compareHands(
  snapshot: RecognitionSnapshot,
  finalTiles: readonly string[],
): { final: number; wrong: number; missing: number; wrongFlagged: number } {
  const proposed = countTiles([...snapshot.tiles, ...snapshot.flowers]);
  const saved = countTiles(finalTiles);
  const flagged = new Set<string>(snapshot.flagged);
  let matched = 0;
  let wrong = 0;
  let wrongFlagged = 0;
  for (const [tile, n] of proposed) {
    const same = Math.min(n, saved.get(tile) ?? 0);
    matched += same;
    const extra = n - same;
    wrong += extra;
    if (flagged.has(tile)) wrongFlagged += extra;
  }
  return { final: finalTiles.length, wrong, missing: finalTiles.length - matched, wrongFlagged };
}

/** The saved hand as one pile: hand tiles, every tile of every set, and flowers. */
export function handTiles(hand: {
  concealed: readonly string[];
  melds: readonly { tiles: readonly string[] }[];
  flowers?: readonly string[];
}): string[] {
  return [...hand.concealed, ...hand.melds.flatMap((m) => m.tiles), ...(hand.flowers ?? [])];
}

const isTileValue = (t: unknown): boolean => typeof t === 'string' && isTile(t);
const isBonusValue = (t: unknown): boolean => typeof t === 'string' && isBonusTile(t);

/** A proposal arriving from another device (a guest's round) — accepted only if it is exactly what this app writes. */
export function sanitiseSnapshot(value: unknown): RecognitionSnapshot | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const v = value as Record<string, unknown>;
  if (v['engine'] !== 'on-device' && v['engine'] !== 'relay') return undefined;
  if (typeof v['timestamp'] !== 'number' || !Number.isFinite(v['timestamp'])) return undefined;
  const list = (x: unknown, ok: (t: unknown) => boolean, max: number): unknown[] | undefined =>
    Array.isArray(x) && x.length <= max && x.every(ok) ? x : undefined;
  const tiles = list(v['tiles'], isTileValue, 40);
  const flowers = list(v['flowers'], isBonusValue, 8);
  const flagged = list(v['flagged'], isTileValue, 40);
  if (!tiles || !flowers || !flagged) return undefined;
  return {
    engine: v['engine'],
    timestamp: v['timestamp'],
    tiles: tiles as Tile[],
    flowers: flowers as BonusTile[],
    flagged: flagged as Tile[],
  };
}

export interface EngineAccuracy {
  engine: RecognitionEngine;
  /** Scanned hands that were saved as a round. */
  handsSaved: number;
  /** Of those, hands needing no correction at all. */
  exactHands: number;
  tilesTotal: number;
  tilesRight: number;
  /** Tiles a person had to change, summed over hands (a swap counts once). */
  corrections: number;
  wrongTiles: number;
  /** Wrong tiles that the scan had itself flagged "double-check". */
  wrongTilesFlagged: number;
  /** The scan found no hand in the photo. */
  noHand: number;
  /** The scan itself failed (model could not load, etc.). */
  errors: number;
  /** Scans that never became a round. */
  discarded: number;
}

export function summariseAccuracy(entries: readonly AccuracyEntry[]): Record<RecognitionEngine, EngineAccuracy> {
  const blank = (engine: RecognitionEngine): EngineAccuracy => ({
    engine,
    handsSaved: 0,
    exactHands: 0,
    tilesTotal: 0,
    tilesRight: 0,
    corrections: 0,
    wrongTiles: 0,
    wrongTilesFlagged: 0,
    noHand: 0,
    errors: 0,
    discarded: 0,
  });
  const out: Record<RecognitionEngine, EngineAccuracy> = { 'on-device': blank('on-device'), relay: blank('relay') };
  for (const e of entries) {
    const s = out[e.engine];
    if (e.kind === 'used') {
      s.handsSaved++;
      if (e.wrong === 0 && e.missing === 0) s.exactHands++;
      s.tilesTotal += e.final;
      s.tilesRight += e.final - e.missing;
      s.corrections += Math.max(e.wrong, e.missing);
      s.wrongTiles += e.wrong;
      s.wrongTilesFlagged += e.wrongFlagged;
    } else if (e.kind === 'miss') {
      if (e.reason === 'error') s.errors++;
      else s.noHand++;
    } else {
      s.discarded++;
    }
  }
  return out;
}

/** Below this many saved hands a percentage is more noise than signal, so it is labelled early. */
export const RELIABLE_AFTER_HANDS = 20;

const ENGINE_LABELS: Record<RecognitionEngine, string> = { 'on-device': 'On-device', relay: 'Relay' };

/**
 * One short line per engine that has saved hands — for the Past Games header. An engine
 * with nothing saved yet is left out rather than shown as "0%" (which would read as a
 * bad score rather than "no data").
 */
export function accuracyHeadlines(
  byEngine: Record<RecognitionEngine, EngineAccuracy>,
): { engine: RecognitionEngine; label: string; text: string }[] {
  const lines: { engine: RecognitionEngine; label: string; text: string }[] = [];
  for (const engine of ['on-device', 'relay'] as const) {
    const a = byEngine[engine];
    if (a.handsSaved === 0 || a.tilesTotal === 0) continue;
    const pct = Math.round((a.tilesRight / a.tilesTotal) * 100);
    const early = a.handsSaved < RELIABLE_AFTER_HANDS ? ' · early numbers' : '';
    lines.push({
      engine,
      label: ENGINE_LABELS[engine],
      text: `${pct}% of tiles right · ${a.exactHands} of ${a.handsSaved} hand${a.handsSaved === 1 ? '' : 's'} needed no correction${early}`,
    });
  }
  return lines;
}

// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------

function defaultStorage(): StorageLike {
  return localStorage;
}

export function readAccuracyLog(storage: StorageLike = defaultStorage()): AccuracyEntry[] {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(LOG_KEY) ?? '[]');
    return Array.isArray(parsed) ? (parsed as AccuracyEntry[]) : [];
  } catch {
    return [];
  }
}

function writeLog(entries: AccuracyEntry[], storage: StorageLike): void {
  try {
    storage.setItem(LOG_KEY, JSON.stringify(entries.slice(-MAX_ENTRIES)));
  } catch {
    // Measuring must never get in the way of scoring a round.
  }
}

function appendLog(entry: AccuracyEntry, storage: StorageLike): void {
  writeLog([...readAccuracyLog(storage), entry], storage);
}

function readPending(storage: StorageLike): Record<string, RecognitionSnapshot> {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(PENDING_KEY) ?? '{}');
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, RecognitionSnapshot>)
      : {};
  } catch {
    return {};
  }
}

function writePending(pending: Record<string, RecognitionSnapshot>, storage: StorageLike): void {
  try {
    storage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch {
    // see writeLog
  }
}

/** Moves scans that are too old (or beyond the cap) out of "waiting" and into the log as discarded. */
function expire(pending: Record<string, RecognitionSnapshot>, now: number, storage: StorageLike): void {
  const keys = Object.keys(pending).sort((a, b) => pending[a]!.timestamp - pending[b]!.timestamp);
  const drop = keys.filter((k, i) => now - pending[k]!.timestamp > PENDING_TTL_MS || keys.length - i > MAX_PENDING);
  if (drop.length === 0) return;
  const log = readAccuracyLog(storage);
  for (const k of drop) {
    log.push({ kind: 'unused', engine: pending[k]!.engine, timestamp: now });
    delete pending[k];
  }
  writeLog(log, storage);
}

/** A scan just produced a hand: remember what it proposed, until the round is saved. */
export function notePendingRecognition(
  photo: string,
  snapshot: RecognitionSnapshot,
  storage: StorageLike = defaultStorage(),
): void {
  const pending = readPending(storage);
  pending[photoKey(photo)] = snapshot;
  expire(pending, snapshot.timestamp, storage);
  writePending(pending, storage);
}

/** How many scans are waiting to be saved as a round (or to age out). */
export function countPendingRecognitions(storage: StorageLike = defaultStorage()): number {
  return Object.keys(readPending(storage)).length;
}

/**
 * The proposal for a photo that is being saved with a round, if this device
 * made one — and forgets it, so it is only ever counted once.
 */
export function takeRecognitionFor(
  photos: readonly string[],
  storage: StorageLike = defaultStorage(),
): RecognitionSnapshot | undefined {
  const pending = readPending(storage);
  let found: RecognitionSnapshot | undefined;
  for (const photo of photos) {
    const key = photoKey(photo);
    const snap = pending[key];
    if (snap) {
      found ??= snap;
      delete pending[key];
    }
  }
  if (found) writePending(pending, storage);
  return found;
}

/** Compares a proposal with the hand that was finally saved, and records the result. */
export function logRecognitionResult(
  snapshot: RecognitionSnapshot,
  finalTiles: readonly string[],
  now: number = Date.now(),
  storage: StorageLike = defaultStorage(),
): void {
  appendLog({ kind: 'used', engine: snapshot.engine, timestamp: now, ...compareHands(snapshot, finalTiles) }, storage);
}

/** A scan that produced nothing usable. */
export function logRecognitionMiss(
  engine: RecognitionEngine,
  reason: 'no-hand' | 'error',
  now: number = Date.now(),
  storage: StorageLike = defaultStorage(),
): void {
  appendLog({ kind: 'miss', engine, timestamp: now, reason }, storage);
}

export function clearAccuracyLog(storage: StorageLike = defaultStorage()): void {
  storage.removeItem(LOG_KEY);
  storage.removeItem(PENDING_KEY);
}
